import React, { useState } from 'react';
import { 
  XMarkIcon,
  DocumentTextIcon, 
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  UserIcon
} from '@heroicons/react/24/outline';

interface DocumentSubmission {
  id: number;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedAt: string;
  note?: string;
}

interface DocumentRequestItem {
  id: number;
  requestId: number;
  documentTypeId: number;
  status: 'pending' | 'submitted' | 'accepted' | 'rejected';
  submittedAt?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  documentType: {
    name: string;
    description?: string;
    isRequired: boolean;
    maxFileSizeMb: number;
    allowedFileTypes: string[];
  };
  submissions?: DocumentSubmission[];
  notes?: Array<{
    note: string;
    createdAt: string;
  }>;
}

interface DocumentRequest {
  id: number;
  organizationId: number;
  leadId: number;
  requestToken: string;
  status: 'requested' | 'submitted' | 'reviewed' | 'rejected';
  requestedAt: string;
  lead: {
    firstName: string;
    lastName: string;
    email: string;
  };
  items: DocumentRequestItem[];
}

interface ManualDocumentUploadModalProps {
  request: DocumentRequest;
  onClose: () => void;
  onUploadSuccess: () => void;
}

interface FileUpload {
  file: File;
  preview: string;
  error?: string;
}

const ManualDocumentUploadModal: React.FC<ManualDocumentUploadModalProps> = ({ 
  request, 
  onClose, 
  onUploadSuccess 
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [uploads, setUploads] = useState<{ [key: number]: FileUpload[] }>({});
  const [notes, setNotes] = useState<{ [key: number]: string }>({});

  const handleFileSelect = (itemId: number, files: FileList | null) => {
    if (!files) return;

    const newUploads: FileUpload[] = [];
    const item = request?.items.find(i => i.id === itemId);
    if (!item) return;

    Array.from(files).forEach(file => {
      // Validate file size
      if (file.size > item.documentType.maxFileSizeMb * 1024 * 1024) {
        newUploads.push({
          file,
          preview: URL.createObjectURL(file),
          error: `File size exceeds ${item.documentType.maxFileSizeMb}MB limit`
        });
        return;
      }

      // Validate file type
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !item.documentType.allowedFileTypes.includes(fileExtension)) {
        newUploads.push({
          file,
          preview: URL.createObjectURL(file),
          error: `File type not allowed. Allowed types: ${item.documentType.allowedFileTypes.join(', ')}`
        });
        return;
      }

      newUploads.push({
        file,
        preview: URL.createObjectURL(file)
      });
    });

    setUploads(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...newUploads]
    }));
  };

  const removeFile = (itemId: number, index: number) => {
    setUploads(prev => {
      const newUploads = { ...prev };
      if (newUploads[itemId]) {
        newUploads[itemId] = newUploads[itemId].filter((_, i) => i !== index);
      }
      return newUploads;
    });
  };

  const toggleItemSelection = (itemId: number) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
        // Remove uploads when deselecting
        setUploads(prevUploads => {
          const newUploads = { ...prevUploads };
          delete newUploads[itemId];
          return newUploads;
        });
        // Remove notes when deselecting
        setNotes(prevNotes => {
          const newNotes = { ...prevNotes };
          delete newNotes[itemId];
          return newNotes;
        });
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!request) return;

    // Check if at least one item is selected
    if (selectedItems.size === 0) {
      setError('Please select at least one document type to upload');
      return;
    }

    // Check if all selected items have files uploaded
    const selectedItemsArray = Array.from(selectedItems);
    const itemsWithFiles = selectedItemsArray.filter(itemId => {
      const itemUploads = uploads[itemId]?.filter(u => !u.error) || [];
      return itemUploads.length > 0;
    });

    if (itemsWithFiles.length === 0) {
      setError('Please upload at least one file for the selected document types');
      return;
    }

    // Check if any selected item is missing files
    const itemsWithoutFiles = selectedItemsArray.filter(itemId => {
      const itemUploads = uploads[itemId]?.filter(u => !u.error) || [];
      return itemUploads.length === 0;
    });

    if (itemsWithoutFiles.length > 0) {
      const missingItems = itemsWithoutFiles.map(itemId => {
        const item = request.items.find(i => i.id === itemId);
        return item?.documentType.name;
      }).filter(Boolean);
      setError(`Please upload files for: ${missingItems.join(', ')}`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Prepare form data for file upload
      const formData = new FormData();
      
      // Add items data with file counts (only for selected items with files)
      const items = selectedItemsArray
        .map(itemId => {
          const item = request.items.find(i => i.id === itemId);
          if (!item) return null;
          const itemUploads = uploads[itemId]?.filter(u => !u.error) || [];
          return {
            requestItemId: item.id,
            note: notes[item.id] || '',
            fileCount: itemUploads.length
          };
        })
        .filter(Boolean) as Array<{ requestItemId: number; note: string; fileCount: number }>;
      
      formData.append('items', JSON.stringify(items));

      // Add files in order by selected document types
      selectedItemsArray.forEach(itemId => {
        const itemUploads = uploads[itemId]?.filter(u => !u.error) || [];
        itemUploads.forEach(upload => {
          formData.append('files', upload.file);
        });
      });

      // Submit to API
      const response = await import('../services/api').then(module => 
        module.default.post(`/documents/requests/${request.id}/manual-submit`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
      );

      if (response.data.success) {
        setSuccess(true);
        // Wait a moment to show success, then close and refresh
        setTimeout(() => {
          onUploadSuccess();
          onClose();
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err: any) {
      console.error('Error uploading documents:', err);
      setError(err.response?.data?.message || 'Failed to upload documents. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
          <div className="text-center py-8">
            <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Documents Uploaded Successfully
            </h3>
            <p className="text-gray-600">
              Documents have been uploaded on behalf of {request.lead.firstName} {request.lead.lastName}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Upload Documents on Behalf of Client
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Uploading for: {request.lead.firstName} {request.lead.lastName} ({request.lead.email})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900 font-medium mb-1">Select Document Types</p>
            <p className="text-xs text-blue-700">
              Select the document types you want to upload files for. You can select multiple types.
            </p>
          </div>

          {request.items.map((item) => {
            const isSelected = selectedItems.has(item.id);
            return (
              <div key={item.id} className={`border rounded-lg p-4 ${isSelected ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3 flex-1">
                    <input
                      type="checkbox"
                      id={`select-${item.id}`}
                      checked={isSelected}
                      onChange={() => toggleItemSelection(item.id)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <label htmlFor={`select-${item.id}`} className="cursor-pointer">
                        <h4 className="text-sm font-medium text-gray-900 flex items-center">
                          <DocumentTextIcon className="w-4 h-4 mr-2 text-gray-400" />
                          {item.documentType.name}
                          {item.documentType.isRequired && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Required
                            </span>
                          )}
                        </h4>
                        {item.documentType.description && (
                          <p className="text-xs text-gray-600 mt-1">{item.documentType.description}</p>
                        )}
                      </label>
                      
                      {/* Show existing submissions if any */}
                      {item.submissions && item.submissions.length > 0 && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                          <p className="font-medium text-blue-900 mb-1">
                            Existing submissions ({item.submissions.length}):
                          </p>
                          <ul className="text-blue-700 space-y-1">
                            {item.submissions.map((submission, idx) => (
                              <li key={idx}>
                                • {submission.fileName} ({formatFileSize(submission.fileSize)})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Display rejection reasons/notes */}
                      {item.notes && item.notes.length > 0 && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                          <div className="flex items-start">
                            <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                            <div className="flex-1">
                              {item.notes.map((note, index) => (
                                <p key={index} className="text-yellow-700 mb-1">
                                  {note.note}
                                </p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    item.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                    item.status === 'accepted' ? 'bg-green-100 text-green-800' :
                    item.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {item.status}
                  </span>
                </div>

                {isSelected && (
                  <div className="space-y-3 mt-3 pt-3 border-t border-blue-200">
                    {/* File Upload */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Upload Files (Add more files)
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                        <CloudArrowUpIcon className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-600 mb-1">
                          Click to upload or drag and drop files here
                        </p>
                        <p className="text-xs text-gray-500 mb-2">
                          Max size: {item.documentType.maxFileSizeMb}MB | 
                          Allowed types: {item.documentType.allowedFileTypes.join(', ')}
                        </p>
                        <input
                          type="file"
                          multiple
                          accept={item.documentType.allowedFileTypes.map(type => `.${type}`).join(',')}
                          onChange={(e) => handleFileSelect(item.id, e.target.files)}
                          className="hidden"
                          id={`file-upload-${item.id}`}
                        />
                        <label
                          htmlFor={`file-upload-${item.id}`}
                          className="mt-1 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
                        >
                          Choose Files
                        </label>
                      </div>
                    </div>

                    {/* Uploaded Files */}
                    {uploads[item.id] && uploads[item.id].length > 0 && (
                      <div className="space-y-2">
                        <h5 className="text-xs font-medium text-gray-700">New Files to Upload:</h5>
                        {uploads[item.id].map((upload, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-xs font-medium text-gray-900">{upload.file.name}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(upload.file.size)}</p>
                                {upload.error && (
                                  <p className="text-xs text-red-600">{upload.error}</p>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(item.id, index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <XCircleIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Note */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Note (Optional)
                      </label>
                      <textarea
                        value={notes[item.id] || ''}
                        onChange={(e) => setNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                        rows={2}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Add any additional notes about this document..."
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </div>
              ) : (
                'Upload Documents'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualDocumentUploadModal;

